/**
 * Phone canonicalization for feature-cohort membership.
 *
 * Canonical form = the WhatsApp wa_id: E.164 digits with NO leading '+'
 * (e.g. "34636115993"). Stored verbatim in `feature_cohort_members.phone_digits`
 * so the bot's lookup is an exact string match on the wa_id it already has.
 *
 * Deliberately strict: a number without a country code is REJECTED rather than
 * guessed (the contact picker is the zero-ambiguity path — dashboard contact
 * records carry the wa_id itself). Mirrors the DB check `^[0-9]{7,15}$`.
 */

export type NormalizedPhone =
  | { ok: true; digits: string; display: string }
  | { ok: false; error: string };

export const normalizePhoneDigits = (input: string): NormalizedPhone => {
  const trimmed = (input ?? "").trim();
  if (!trimmed) {
    return { ok: false, error: "Enter a phone number" };
  }
  // Strip separators people paste: spaces, dashes, dots, parentheses.
  let cleaned = trimmed.replace(/[\s\-.()]/g, "");
  const hadPlus = cleaned.startsWith("+");
  if (hadPlus) {
    cleaned = cleaned.slice(1);
  } else if (cleaned.startsWith("00")) {
    // International dialing prefix
    cleaned = cleaned.slice(2);
  }
  if (!/^[0-9]+$/.test(cleaned)) {
    return {
      ok: false,
      error: "Only digits (and an optional leading +) are allowed",
    };
  }
  if (cleaned.startsWith("0")) {
    return {
      ok: false,
      error:
        "Looks like a national number — enter the full international number, e.g. +34 636 115 993",
    };
  }
  if (cleaned.length < 7 || cleaned.length > 15) {
    return {
      ok: false,
      error:
        "Enter the full international number (7–15 digits), e.g. +34 636 115 993",
    };
  }
  // Without an explicit '+'/'00' we accept only lengths that can't be a bare
  // national number (>= 10 digits, like a wa_id) — shorter input must carry
  // the prefix so we never guess a country code.
  if (!hadPlus && cleaned.length < 10) {
    return {
      ok: false,
      error:
        "Add the country code — enter the full international number, e.g. +34 636 115 993",
    };
  }
  return { ok: true, digits: cleaned, display: `+${cleaned}` };
};

export const displayPhone = (digits: string): string => `+${digits}`;
